import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { BackendDelegation, LiveConversation } from '@voiceai/wire'
import { sessions } from '@/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { fmtClock, fmtMs } from '@/components/state-pill'
import type { TranscriptToolCall } from './transcript-order'
import { toolDump } from './transcript-order'
import { cn } from '@/lib/utils'

function DelegationCard({ d, tools }: { d: BackendDelegation; tools: TranscriptToolCall[] }) {
  const calls = tools.filter(t => d.toolCallIds.includes(t.callId))
  const elapsed = d.startedAt !== null && d.endedAt !== null && d.endedAt >= d.startedAt ? d.endedAt - d.startedAt : null
  return <details className="my-2 rounded-lg border bg-muted/30 p-3">
    <summary className="cursor-pointer text-sm">
      <span className="ml-2 inline-flex flex-wrap items-center gap-2">
        <Badge variant="outline">Backend</Badge>
        <strong>{d.model ?? 'Model not observed'}</strong>
        <Badge variant="secondary">{d.status === 'unverified' ? 'Completion not observed' : d.status}</Badge>
        {elapsed !== null && <span className="text-muted-foreground">{fmtMs(elapsed)}</span>}
        {d.startedAt !== null && <span className="font-mono text-xs text-muted-foreground">{fmtClock(d.startedAt)}</span>}
      </span>
    </summary>
    <div className="mt-3 flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">Backend result · not spoken audio. Nearby conversation provides context; the exact backend input is not exposed.</p>
      <p className="whitespace-pre-wrap break-words font-mono text-sm">{d.text || 'No backend text was captured.'}</p>
      {d.text && !d.outputComplete && <p className="text-xs text-muted-foreground">Partial text: no final text event was observed.</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Started: {d.startedAt === null ? 'not observed' : fmtClock(d.startedAt)}</span>
        <span>Finished: {d.endedAt === null ? 'not observed' : fmtClock(d.endedAt)}</span>
        <span>Input tokens: {d.inputTokens ?? 'not observed'}</span>
        <span>Output tokens: {d.outputTokens ?? 'not observed'}</span>
        <span>Reasoning tokens: {d.reasoningTokens ?? 'not observed'}</span>
      </div>
      <p className="text-xs text-muted-foreground">Duration measures observed backend work, not time until the caller heard the answer. Internal reasoning is not displayed.</p>
      {calls.map(c => <pre key={c.callId} className="overflow-auto whitespace-pre-wrap break-words rounded border p-2 text-xs">{toolDump(c)}</pre>)}
      {d.toolCallIds.length > calls.length && <p className="text-xs text-muted-foreground">Some tool-call records are unavailable.</p>}
      <p className="break-all font-mono text-xs text-muted-foreground">Delegation: {d.id}<br />Response: {d.responseId ?? 'not observed'}</p>
    </div>
  </details>
}

/** Checkpoint-backed view, intentionally independent of the original transcript:
 * failure to fetch the log must never hide the already available conversation. */
export function LiveConversationThread({ id, ended, tools, children, playheadEpochMs }: {
  id: string; ended: boolean; tools: TranscriptToolCall[]; children: ReactNode; playheadEpochMs: number | null
}) {
  const [enabled, setEnabled] = useState(true)
  const [data, setData] = useState<LiveConversation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const activeRow = useRef<HTMLDivElement | null>(null)
  const activeSpeech = playheadEpochMs === null ? null : data?.items.filter(i => i.kind === 'speech' && i.at <= playheadEpochMs).at(-1)?.id
  useEffect(() => {
    if (enabled && activeSpeech) activeRow.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [enabled, activeSpeech])
  useEffect(() => {
    if (!enabled) return
    const ac = new AbortController()
    setLoading(true)
    setError(null)
    sessions.delegations(id, ac.signal).then(value => { if (!ac.signal.aborted) setData(value) })
      .catch((e: unknown) => { if (!ac.signal.aborted) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (!ac.signal.aborted) setLoading(false) })
    return () => ac.abort()
  }, [id, enabled, refresh, ended])
  useEffect(() => {
    if (ended || !enabled) return
    const timer = setInterval(() => setRefresh(v => v + 1), 30_000)
    return () => clearInterval(timer)
  }, [ended, enabled])
  const count = data?.items.filter(i => i.kind === 'delegation').length ?? 0
  return <div className="flex flex-col gap-3">
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" size="sm" aria-pressed={enabled} onClick={() => setEnabled(v => !v)}>
        {enabled ? 'Hide backend activity' : 'Show backend activity'}
      </Button>
      {enabled && <Button variant="ghost" size="sm" disabled={loading} onClick={() => setRefresh(v => v + 1)}>{loading ? 'Loading…' : 'Refresh activity'}</Button>}
      {enabled && data && <span className="text-xs text-muted-foreground">{count} delegation{count === 1 ? '' : 's'} recorded</span>}
    </div>
    {enabled && error && <Alert><AlertDescription>Backend activity could not be refreshed: {error} {data ? 'Showing the previous checkpoint.' : 'The saved transcript remains below.'}</AlertDescription></Alert>}
    {enabled && data && <p className="text-xs text-muted-foreground">Speech and backend activity ordered by sideband receipt time; overlapping speech is grouped approximately. Latest saved event: {data.checkpointAt === null ? 'unknown' : fmtClock(data.checkpointAt)}.{!ended && ' Updates follow saved checkpoints, approximately every 30 seconds.'}</p>}
    {enabled && data && (data.malformedLines > 0 || data.captureGaps > 0) && <Alert><AlertDescription>Capture may be incomplete: {data.malformedLines} unreadable event lines and {data.captureGaps} capture interruptions. Missing evidence is not proof that no work happened.</AlertDescription></Alert>}
    {enabled && data && data.items.length > 0 ? <div className="flex flex-col gap-1">
      {data.items.map(item => item.kind === 'delegation' ? <DelegationCard key={item.id} d={item.delegation} tools={tools} />
        : item.kind === 'gap' ? <Alert key={item.id}><AlertDescription>{fmtClock(item.at)} · Capture interruption: {item.label}</AlertDescription></Alert>
        : <div key={item.id} ref={item.id === activeSpeech ? activeRow : undefined} className={cn('flex items-start gap-3 rounded-md px-2 py-2', item.id === activeSpeech && 'bg-accent')}>
          <Badge variant={item.role === 'user' ? 'default' : 'secondary'}>{item.role === 'user' ? 'user' : 'agent'}</Badge>
          <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm">{item.text}</p>
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{fmtClock(item.at)}</span>
        </div>)}
    </div> : children}
  </div>
}
